import { memo, useReducer } from 'react';
import { OverlayTrigger, Tooltip, Popover } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { BsThreeDotsVertical } from 'react-icons/bs';

import baseStyle from '../../styles/base.module.scss';
import ModifyImputation from '../../../ApprovalPending/ViewApprovalPendingContainer/ButtonsApprovalPending/ModifyImputation/ModifyImputation';
import { optimisticInitialState, optimisticUIReducer } from '../../reducer/optimisticUIReducer';
import { weeklyWorkReportColors } from '../../../../utils/dataStatus';

import styles from './PositionLine.module.scss';

/**
 * @typedef Props
 * @property {boolean} cambioImputacion
 * @property {Date} date
 * @property {boolean} editaImputacion
 * @property {(id: string) => void} handleLine
 * @property {boolean} isChecked
 * @property {boolean} isDisabled
 * @property {boolean} isInspector
 * @property {string} line
 * @property {string} lineID
 * @property {string} measureUnit
 * @property {string} position
 * @property {boolean} showHeader
 * @property {import('../../hooks/useSort').SortType} sortType
 * @property {string} title
 * @property {string} titleSortBy
 * @property {import('../../../../adapters/weekly-report/get-weekly-report-adapter').PositionLine["weeklyReport"]} weeklyReport
 */

/** @param {Props} */
const PositionLine = ({
  cambioImputacion,
  date,
  editaImputacion,
  handleLine,
  isChecked,
  isDisabled,
  isInspector,
  line,
  lineID,
  measureUnit,
  position,
  titleSortBy,
  weeklyReport
}) => {
  const [changedActions, dispatch] = useReducer(optimisticUIReducer, {
    ...optimisticInitialState,
    iconColor: cambioImputacion
      ? weeklyWorkReportColors.imputation.weekly
      : optimisticInitialState.iconColor
  });
  const [lineNumber, ...restLine] = line.split(' - ');
  const [positionNumber, ...restPosition] = position.split(' - ');

  const lineDescription = restLine.join(' - ');
  const positionDescription = restPosition.join(' - ');
  const [showActionsModal, toggleActionsModal] = useReducer((state) => !state, false);

  const handleCheckBox = () => {
    handleLine(lineID);
  };

  /** @param {import('../../reducer/optimisticUIReducer').OptimisticUIActions} action */
  const handleChangedActions = (action) => {
    dispatch(action);
  };

  const weeklyReportIDs = weeklyReport.reduce((arr, day) => {
    if (day?.idParteDiario) arr.push(day.idParteDiario);

    return arr;
  }, []);

  return (
    <td colSpan={1} data-row-col={1}>
      <div className={styles['position-line-container']}>
        <div className={styles['position-line-checkbox-container']}>
          <input
            checked={isChecked}
            className={baseStyle['focus-ring']}
            disabled={isDisabled}
            onChange={handleCheckBox}
            type="checkbox"
          />
        </div>
        <div className={styles['position-line-details-container']}>
          <div className={styles['position-container']}>
            {positionDescription.length > 43 ? (
              <OverlayTrigger
                delay={{ show: 250, hide: 400 }}
                overlay={<Tooltip id="position-tooltip">{positionDescription}</Tooltip>}
                placement="top"
              >
                <span className={styles.text}>
                  <span className={styles.position}>P{positionNumber}</span> {positionDescription}
                </span>
              </OverlayTrigger>
            ) : (
              <span className={styles.text}>
                <span className={styles.position}>P{positionNumber}</span> {positionDescription}
              </span>
            )}
            {isInspector && (
              <OverlayTrigger
                onToggle={toggleActionsModal}
                overlay={
                  <Popover
                    className={styles.popover}
                    id="popover-positioned-left"
                    onClick={toggleActionsModal}
                  >
                    <Popover.Body>
                      <ModifyImputation
                        changedActions={changedActions}
                        dailyWorkReport={{
                          cambioImputacion,
                          editaImputacion,
                          fecha: date,
                          idParteDiario: weeklyReportIDs,
                          lineaSap: line,
                          posicionSap: position,
                          ubicacion: titleSortBy,
                          weekly: true
                        }}
                        handleChangedActions={handleChangedActions}
                        isWeekly
                      />
                    </Popover.Body>
                  </Popover>
                }
                placement="left"
                rootClose
                show={showActionsModal}
                trigger="click"
              >
                <button
                  aria-label="Acciones"
                  className={`${baseStyle['focus-ring']} ${styles.btn}`}
                  type="button"
                >
                  <BsThreeDotsVertical />
                </button>
              </OverlayTrigger>
            )}
          </div>
          <div className={styles['line-container']}>
            {lineDescription.length > 43 ? (
              <OverlayTrigger
                delay={{ show: 250, hide: 400 }}
                overlay={<Tooltip id="line-tooltip">{lineDescription}</Tooltip>}
                placement="top"
              >
                <span className={styles.text}>
                  <span className={styles.line}>L{lineNumber}</span> {lineDescription}
                </span>
              </OverlayTrigger>
            ) : (
              <span className={styles.text}>
                <span className={styles.line}>L{lineNumber}</span> {lineDescription}
              </span>
            )}
            <div className={styles['line-details-container']}>
              <span>UM</span>
              <span>{measureUnit}</span>
            </div>
          </div>
        </div>
      </div>
    </td>
  );
};

PositionLine.propTypes = {
  cambioImputacion: PropTypes.bool.isRequired,
  date: PropTypes.instanceOf(Date).isRequired,
  editaImputacion: PropTypes.bool.isRequired,
  handleLine: PropTypes.func.isRequired,
  isChecked: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isInspector: PropTypes.bool.isRequired,
  line: PropTypes.string.isRequired,
  lineID: PropTypes.string.isRequired,
  measureUnit: PropTypes.string.isRequired,
  position: PropTypes.string.isRequired,
  titleSortBy: PropTypes.string.isRequired,
  weeklyReport: PropTypes.arrayOf.isRequired,
  requestData: PropTypes.shape({ nroSolicitud: PropTypes.string }).isRequired,
  secondColumnData: PropTypes.shape({}).isRequired
};

export default memo(
  PositionLine,
  (prev, next) => prev.isChecked === next.isChecked && prev.isDisabled === next.isDisabled
);
